"use client";

/**
 * Streaming comparison surface (B-P0-09 v2, 2026-05-15).
 *
 * Single persistent component tree from mount through stream
 * complete. ONE <Hero> mounted once. ONE <ComparisonRow> per
 * retailer mounted once. Every visible change is a prop / state
 * update on the SAME DOM node - never a component swap, never a
 * remount, never an entrance-animation re-fire.
 *
 * Lifecycle:
 *   1. mount -> openSearchStream(...)
 *   2. `intent` event -> Hero's `group.canonical_name` updates in
 *      place. No remount.
 *   3. `targets` event -> set retailer-keyed slots. The bento grid
 *      pre-allocates N <ComparisonRow loadingState="loading"
 *      retailerSlug=...> mounts. From this moment forward every
 *      slot's DOM node persists; only its `product` prop changes.
 *   4. each `result` event -> the matching slot's `product` prop
 *      flips from undefined to the TFP-enriched ProductPageData.
 *      ComparisonRow internally crossfades from "checking now"
 *      to the loaded card. Same DOM node, same React key.
 *   5. each `failure` event -> the matching slot is removed from
 *      the rendered list. <AnimatePresence> smoothly fades it out.
 *   6. `complete` event -> Hero swaps to final.groups[0] data.
 *      Same mount, prop change only. <AnimatePresence layout>
 *      smoothly transitions any sort-order changes.
 *
 * Why this avoids the blink: HeroSkeleton / ComparisonRowSkeleton
 * are deleted. The Hero and ComparisonRow components handle their
 * own loading view internally. React reuses the same DOM nodes
 * across state transitions, so framer-motion's `initial -> animate`
 * fires exactly once for the page lifetime, not once per swap.
 *
 * Fallback: if the stream FAILS TO OPEN (HTTP non-2xx), the
 * component falls back to the legacy non-streaming endpoint. A
 * mid-stream failure (any error AFTER one event has been received)
 * does NOT fall back - that would fire a duplicate 60-90s search.
 */

import Link from "next/link";
import type { Route } from "next";
import { useRouter } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { ComparisonNotFound } from "@/components/comparison/ComparisonNotFound";
import { ComparisonUpdating } from "@/components/comparison/ComparisonUpdating";
import { Disclosures } from "@/components/comparison/Disclosures";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Hero } from "@/components/comparison/v2/Hero";
import { PriceBeatCallout } from "@/components/comparison/PriceBeat";
import { CheaperElsewhereStrip } from "@/components/comparison/CheaperElsewhereStrip";
import { RetailerListFold } from "@/components/comparison/RetailerListFold";
import { passesWinnerChecks, pickOneCard, sameProductLast } from "@/lib/oneCard";
import { SearchProgress } from "@/components/comparison/SearchProgress";
import { SubmittedClaimsProvider } from "@/components/comparison/SubmittedClaimsProvider";
import { AskPanel } from "@/components/comparison/broadcast/AskPanel";
import { LivePriceCheck } from "@/components/comparison/LivePriceCheck";
import {
  BROADCAST_ENABLED,
  PROGRESSIVE_SEARCH_ENABLED,
  SEARCH_SCENE_ENABLED,
  SLIM_SEARCH_HERO_ENABLED,
  WALK_AWAY_SEARCH_ENABLED,
} from "@/lib/flags";
import { dropCarrierOffers } from "@/components/comparison/carrierFilter";
import {
  dropOutOfStockOffers,
  isBuyableOffer,
} from "@/components/comparison/stockFilter";
import {
  dropUnconfirmedPrices,
  isShownPrice,
} from "@/components/comparison/priceConfirmedFilter";
import { ComparisonRow } from "@/components/comparison/v2/ComparisonRow";
// 2026-09-15 (owner): "Also found" bands hidden until designed; see page.tsx.
// import { AlsoFoundSection } from "@/components/comparison/v2/AlsoFoundSection";
import { ListCard } from "@/components/comparison/v2/ListCard";
import { ListCardSkeleton } from "@/components/comparison/v2/ListCardSkeleton";
import { BreakdownRevealContext } from "@/lib/breakdown-reveal";
import { checkedHeroImage, rejectedHeroImages } from "@/lib/jsonld";
import { prettifyRetailerSlug } from "@/lib/retailers/prettify";
import { ProgressiveBuildingState } from "@/components/comparison/ProgressiveBuildingState";
import { SearchingSceneOverlay } from "@/components/comparison/SearchingSceneOverlay";
import { SearchAgainBar } from "@/components/comparison/SearchAgainBar";
import { WalkAwayTracker } from "@/components/comparison/WalkAwayTracker";
import { beginLiveSearch, endLiveSearch } from "@/lib/liveSearchGuard";
import {
  AtCapacityCard,
  DailyLimitCard,
  GeoBlockCard,
  SignupWallCard,
} from "@/components/comparison/SearchWallCards";
import { trackSearchCompleted, trackSearchFailed } from "@/lib/analytics";
import { postSearchByQuery, SearchError } from "@/lib/api/search";
import {
  backgroundSearch,
  openSearchStream,
  SearchStreamError,
  type SearchStreamEvent,
} from "@/lib/api/search-stream";
import {
  getComparisonBySlug,
  getSlugDetail,
  isRetiredSlug,
} from "@/lib/api/pseo";
import {
  beginSearchHandoff,
  savedCopyIsFromThisSearch,
} from "@/lib/searchHandoff";
import {
  buildListCardEntries,
  sanitiseConstraints,
} from "@/lib/list-entries";
import type {
  EquivalenceGroup,
  ParsedIntent,
  ProductPageData,
  SearchResult,
} from "@/lib/api/types";


/**
 * pSEO Phase 3 (2026-06-12): the streaming surface decides which
 * layout to render based on whether the parsed intent looks like a
 * specific product (brand + model both set) or a category list. This
 * mirrors the publisher's product-vs-list dispatcher in
 * api/app/services/pseo/publisher.py::maybe_publish, so the post-
 * search render and the published /c/list/<slug> page share the same
 * layout shape for the same query.
 *
 * Conservative rule: ONLY render list-style when both fields are
 * explicitly missing. A partial product intent (e.g. brand without a
 * model number) still gets the product layout - those queries usually
 * end up publishing as products once the search disambiguates them.
 */
function isListIntent(intent: ParsedIntent | undefined): boolean {
  if (!intent) return false;
  const brand = (intent.brand || "").trim();
  const model = (intent.model_number || "").trim();
  return !brand && !model;
}

/**
 * Phase 3 (2026-06-08): build the canonical comparison URL given a
 * slug + page_kind. Product slugs live at /c/<slug>; list slugs live
 * at /c/list/<slug>. The api routes (intent + complete events,
 * /api/pseo/slugs/<slug>) include page_kind so the client always
 * routes to the matching renderer.
 *
 * Defaults to product when page_kind is null/undefined so callers
 * that have only the slug still work (pre-Phase-3 wire payloads).
 */
function buildSlugUrl(
  slug: string,
  pageKind: "product" | "list" | "comparison" | null | undefined,
): string {
  if (pageKind === "list") return `/c/list/${slug}`;
  return `/c/${slug}`;
}

/**
 * Option B (pSEO Phase 1, 2026-06-08) -> B-290 item 2 (2026-09-28): once a
 * finished search's page is saved, the search screen BECOMES that saved page.
 *
 * Until 28 Sep this only rewrote the address (history.replaceState) as soon
 * as the page's address existed - even when this search's copy had not been
 * (or would never be) saved - and the screen stayed the search screen, so a
 * reload showed something else (Magnifica S, 28 Sep: another photo, the list
 * unfolded, a different "was" price). Now:
 *
 *   1. Wait for the page to exist (the publisher writes it just AFTER the
 *      complete event) and read its kind and canonical address.
 *   2. Wait until the page's stored copy is THIS search's (server time
 *      against server time, see savedCopyIsFromThisSearch). Nothing switches
 *      before that: an older copy is not what the shopper just saw, and one
 *      24 hours old would start a paid rebuild for a signed-in shopper (the
 *      14 Sep Lancome loop).
 *   3. Switch with a real navigation to the saved page (served from the
 *      stored copy; no search runs), holding the results on screen until the
 *      saved page is drawn so its loading scene never flashes
 *      (switchToSavedPage).
 *
 * Not confirmed within ~30 s, a retired page, or a category list (which can
 * show the category's own saved picks rather than this search): the shopper
 * stays on the result and the address does not change. Cost: at most a
 * dozen cache-only reads per search; never a search.
 *
 * Why replace (not push): back from /c/<slug> goes to where the shopper came
 * from, not to /compare?q=... (the same search again).
 *
 * Postcode (2026-06-08): the switch happens for ANY postcode, to the clean
 * canonical /c/<slug> (the canonical postcode-2000 view); the postcode chip
 * on the Hero routes back to a personalised /compare in one click.
 */
async function maybeRedirectToCanonicalSlug(
  slug: string | null | undefined,
  pageKind: "product" | "list" | "comparison" | null | undefined,
  emptyReason: string | null,
  _postcode: string,
  router: ReturnType<typeof useRouter>,
  isCancelled: () => boolean,
  _committedProduct: boolean = false,
  buildStartedAt: number = 0,
  resultFetchedAt: string | null = null,
): Promise<void> {
  if (!slug) return;
  if (emptyReason) return;
  // Postcode is kept in the function signature so existing callers do
  // not need to change shape, but it is no longer used to gate the
  // redirect. See docstring above for the reasoning. Suppress unused-
  // parameter linting without renaming the prop type. A committed
  // product tap is covered by the product-only switch below.
  void _postcode;
  void _committedProduct;
  if (typeof window === "undefined") return;
  // Phase 3 (2026-06-08): branch the target URL on page_kind. If
  // the caller did not pass page_kind (REST fallback path), the
  // /api/pseo/slugs/<slug> lookup below decides the URL.
  const targetUrlFromArgs = pageKind ? buildSlugUrl(slug, pageKind) : null;
  if (targetUrlFromArgs && window.location.pathname === targetUrlFromArgs) {
    // 2026-08-28 stale-cache fix: the in-place rebuild (Live Prices on a
    // stale /c page) completes ON the canonical path, but Next's client
    // router cache still holds the PRE-rebuild page - so the next soft
    // navigation (Searches-tray click, back button) served the OLD page
    // until a hard refresh (owner-reported, Weber/Canon 2026-08-28).
    // Poll for the republished row, then router.refresh(): a soft
    // refresh that re-fetches the route's server payload (now the fresh
    // page) and purges the stale client router cache. Client component
    // state is preserved, so the completed build view does not blink;
    // no paid work runs (the /c server render is cache-only).
    // 2026-09-14 21:00 Sydney (owner-caught loop, Lancôme): refreshing as
    // soon as the slug EXISTS re-rendered a page that was still serving
    // yesterday's row (the publish had not bound the new row yet), which
    // counted as stale and started another rebuild. Poll the page itself
    // and refresh only when it serves a row written by THIS build (its
    // fetched_at is after the build started); give up quietly after ~30s
    // and stay on the built comparison rather than re-render a stale page.
    const startedAt = buildStartedAt;
    for (const delay of [500, 1000, 1500, 2000, 3000, 4000, 5000, 6000, 7000]) {
      if (isCancelled()) return;
      try {
        const page = await getComparisonBySlug(slug);
        const fetchedAt = page?.fetched_at ? Date.parse(page.fetched_at) : 0;
        if (page && fetchedAt >= startedAt) {
          if (!isCancelled()) router.refresh();
          return;
        }
      } catch {
        // poll errors: fall through to the next delay
      }
      await new Promise((r) => setTimeout(r, delay));
    }
    return;
  }

  let target: string | null = null;
  for (const delay of SAVED_COPY_POLL_MS) {
    if (isCancelled()) return;
    try {
      if (target === null) {
        const detail = await getSlugDetail(slug);
        // 2026-09-05: a retired-slug marker means this URL is a corpse;
        // never switch onto it - stay on the live /compare view.
        if (detail && isRetiredSlug(detail)) return;
        if (detail) {
          // A list page is never switched to. The 2026-08-16 never-list
          // contract already barred it for a committed product tap (the
          // list-loop), and for any search the list page can show the
          // category's own saved picks instead of this search (B-290 item
          // 2; 0 searches ended on a list page in the 30 days to 28 Sep).
          if (detail.page_kind !== "product") return;
          target = detail.canonical_slug || slug;
        }
      }
      if (target !== null) {
        const page = await getComparisonBySlug(target);
        if (
          page &&
          savedCopyIsFromThisSearch(page.fetched_at, resultFetchedAt, buildStartedAt)
        ) {
          if (isCancelled()) return;
          if (window.location.pathname !== buildSlugUrl(target, "product")) {
            switchToSavedPage(router, target);
          }
          return;
        }
      }
    } catch {
      // getSlugDetail / getComparisonBySlug already swallow errors and
      // return null; this catch is belt-and-braces (e.g. fetch abort).
    }
    await new Promise((r) => setTimeout(r, delay));
  }
  // Not confirmed in ~30 s: the page was not saved from this search. The
  // shopper stays on the result and the address does not change.
}

/**
 * B-290 item 2: the gaps between checks for this search's saved copy - dense
 * at first (the publisher usually lands within a second or two), ~30 s in
 * all. Each check is one or two cache-only reads.
 */
const SAVED_COPY_POLL_MS = [250, 500, 750, 1000, 1500, 2000, 3000, 4000, 5000, 6000, 7000];

/**
 * B-290 item 2: the longest the results are held on screen while the saved
 * page is drawn. A normal switch takes well under a second (a saved page
 * loads in 0.25-0.57 s); this only bounds a slow one, after which the switch
 * finishes as an ordinary navigation.
 */
const SWITCH_HOLD_MS = 3_000;

/**
 * B-290 item 2: switch the screen to the saved product page. A plain
 * navigation would show /c/<slug>/loading.tsx (the "Hunting your best deal"
 * scene) between the results and the saved page - it looks like a new
 * search starting. The browser's page-switch feature (View Transitions)
 * keeps the results on screen until the saved page is in the document, then
 * cross-fades. Browsers without it (older than Chrome/Edge 111, Safari 18,
 * Firefox 144) switch the ordinary way.
 *
 * The hand-over marker is set first, so the saved page knows this is the
 * same look continuing and records nothing new (lib/searchHandoff.ts).
 */
function switchToSavedPage(
  router: ReturnType<typeof useRouter>,
  slug: string,
): void {
  beginSearchHandoff(slug);
  const go = () => router.replace(buildSlugUrl(slug, "product") as Route);
  if (typeof document.startViewTransition !== "function") {
    go();
    return;
  }
  try {
    const transition = document.startViewTransition(async () => {
      go();
      await waitForSavedPage(slug, SWITCH_HOLD_MS);
    });
    // A skipped switch (hidden tab, another switch) still runs the update;
    // its promises reject by design and must not reach the console.
    transition.ready.catch(() => undefined);
    transition.finished.catch(() => undefined);
    transition.updateCallbackDone.catch(() => undefined);
  } catch {
    go();
  }
}

/** Resolves when the saved page's <main data-saved-page="<slug>"> is in the
 *  document, or after `timeoutMs`. Never rejects. */
function waitForSavedPage(slug: string, timeoutMs: number): Promise<void> {
  const selector = `main[data-saved-page="${slug.replace(/["\\]/g, "\\$&")}"]`;
  return new Promise<void>((resolve) => {
    if (document.querySelector(selector)) {
      resolve();
      return;
    }
    let done = false;
    let timer = 0;
    let observer: MutationObserver | null = null;
    const finish = () => {
      if (done) return;
      done = true;
      observer?.disconnect();
      window.clearTimeout(timer);
      resolve();
    };
    observer = new MutationObserver(() => {
      if (document.querySelector(selector)) finish();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    timer = window.setTimeout(finish, timeoutMs);
  });
}

/**
 * Cache fallback (2026-06-28, P2 fix). A live search that comes up empty but
 * resolved a canonical slug whose SAVED comparison is warm and non-empty
 * should land the user on that /c/<slug> page, not a 0-result dead end.
 *
 * This is the safety net behind the "Nintendo Switch 2 Console OLED"
 * incident: clarify produced a non-existent variant, the live search for it
 * returned 0, yet a healthy multi-retailer pSEO page for the real product
 * existed and was never used. /c/<slug> is cache-only (no live re-search),
 * so this navigation is cheap and cannot loop. Returns true when it
 * navigated away (the caller then keeps showing the "updating" state until
 * the page reloads).
 */
async function fallbackToCachedSlugIfEmpty(
  slug: string | null | undefined,
  pageKind: "product" | "list" | "comparison" | null | undefined,
  result: SearchResult | null,
  emptyReason: string | null,
  isCancelled: () => boolean,
  committedProduct: boolean = false,
): Promise<boolean> {
  const liveEmpty =
    !!emptyReason ||
    !result ||
    result.groups.length === 0 ||
    (result.success_count ?? 0) === 0;
  if (!liveEmpty || !slug || typeof window === "undefined") return false;
  try {
    const cached = await getComparisonBySlug(slug);
    if (isCancelled()) return false;
    const cachedResult = cached?.result
      ? (cached.result as unknown as SearchResult)
      : undefined;
    if (
      cachedResult &&
      cachedResult.groups.length > 0 &&
      (cachedResult.success_count ?? 0) > 0
    ) {
      // 2026-08-16 never-list contract: a committed product tap must never be
      // dumped on a list page (the loop). If the only warm cache for this query
      // is a list, do NOT navigate; the caller shows the honest empty state, not
      // a bounce back to the list the user tapped from.
      const resolvedKind = cached?.page_kind ?? pageKind ?? "product";
      if (committedProduct && resolvedKind === "list") return false;
      // Hard navigation: the intent event already replaceState'd the URL to
      // /c/<slug>, leaving Next's router state on /compare. A full assign
      // deterministically loads the cache-only /c page (no router desync, no
      // live re-search).
      window.location.assign(buildSlugUrl(slug, resolvedKind));
      return true;
    }
  } catch {
    // fall through to the empty state
  }
  return false;
}

interface Props {
  query: string;
  postcode: string;
  initialIntent?: ParsedIntent;
  // B-098: the signed-in user's Clerk id, resolved server-side in the
  // /compare page and passed down (null when anonymous). Forwarded to the
  // search calls so the api counts this search against the right
  // allowance. Resolved on the server to avoid a client useAuth() (which
  // would throw when ClerkProvider is absent in the local no-Clerk path).
  userId?: string | null;
  // 2026-07-12: when true, the search bypasses the comparison cache and
  // re-extracts fresh (overwriting the cached row). Set by the "Search
  // Again" CTA re-checking the SAME product (the ?r= nonce case).
  forceFresh?: boolean;
  // 2026-08-16: true when this /compare search is a committed product tap
  // (from_chip: a broad-list card or a clarify chip). Enforces the never-list
  // contract - the search may resolve to a product/comparison page but is NEVER
  // routed or dumped onto a list page (that is the list-loop the user reported).
  committedProduct?: boolean;
  // 2026-08-19: seed this stream from a category pick's KNOWN shops (skip
  // discovery). Set by a category tap (BroadStreamView / BroadListCards) so the
  // comparison streams real final prices of already-verified shops and cannot
  // dead-end. Forwarded verbatim to the stream request; server resolves the URLs.
  seedPick?: { list_slug: string; title: string };
  // 2026-08-28 Option A (stale /c rebuild): the saved page's hero image,
  // shown from the FIRST frame - the rebuild is otherwise an identical twin
  // of a fresh search. Display-only; no extra fetches, no extra cost.
  seed?: ComparisonSeed;
  // B-240 step 3 (2026-09-18): the one-card switch, so the live view behaves
  // exactly like the saved page: winner rule, "Cheaper elsewhere" strip,
  // folded list. 2026-09-19: the switch lives on the API; a page-backed
  // rebuild passes the page's `layout` here, and a fresh /compare search
  // learns it from the stream's `intent` event (both the same API setting).
  oneCard?: boolean;
  // 2026-08-28 Option A safety valve: fired once when a PAGE-BACKED rebuild
  // (seed present) dead-ends - empty result, wall, or hard error. The parent
  // (ProductLiveBuild) restores the saved page it still holds, so a failed
  // rebuild can never strand the visitor on "couldn't find it" for a product
  // whose page exists. Terminal surfaces are suppressed while seeded so
  // nothing flashes before the restore.
  onDeadEnd?: () => void;
  /** 2026-09-16 (leak 3, B-234): attach to the rebuild already running for
   *  this page instead of starting one. The server never starts a paid
   *  search for this request; with nothing to attach to it answers
   *  `nothing_to_attach` and onDeadEnd restores the saved page. */
  attachOnly?: boolean;
  /** 2026-09-20: the saved page this run rebuilds (its /c slug). */
  pageSlug?: string | null;
}

export interface ComparisonSeed {
  /** The stale page's hero image - the hero shows it from frame one. */
  imageUrl: string | null;
  /** 2026-08-28 Option A (owner decision): EMPTY on the stale-rebuild path.
   *  The rebuild renders as an identical twin of a fresh search (slim hero +
   *  checking strips), upgraded only by the saved photo - old prices appear
   *  nowhere. The full-row seeding this field enabled was rejected as
   *  inconsistent with the fresh experience; the machinery remains for
   *  possible future use but no caller passes rows today. */
  products: ProductPageData[];
}

/** One display slot per seeded retailer: full old product attached, state
 *  "pending" so the progress strip counts it and fresh results upsert over
 *  it. Deduped by retailer (mirrors the targets-event dedupe). */
function slotsFromSeed(seed?: ComparisonSeed): RetailerSlot[] {
  if (!seed || seed.products.length === 0) return [];
  const seen = new Set<string>();
  const out: RetailerSlot[] = [];
  for (const p of seed.products) {
    if (seen.has(p.retailer_slug)) continue;
    seen.add(p.retailer_slug);
    out.push({
      retailer_slug: p.retailer_slug,
      product_url: p.product_url,
      state: "pending",
      product: p,
      seeded: true,
    });
  }
  return out;
}

/** 2026-08-28 (owner design decision, replaced the per-row "Checking price"
 *  chips - stamping every waiting row read as clutter): while a seeded row's
 *  fresh price is in flight there is NO per-row marker; the amber progress
 *  strip under the hero is the page's single "working" signal. Instead, the
 *  MOMENT a visible row's fresh price lands it announces itself with this
 *  soft amber glow blooming and fading over ~1.2s (departures-board pattern:
 *  change itself is the indicator). Mounted keyed by the update timestamp;
 *  ends at opacity 0 and stays inert. */
/** 2026-08-28 slim searching hero (owner-approved Idea 1): while a fresh
 *  search has NO photo and NO priced winner, the hero's dead skeleton
 *  blocks are replaced by this strip - eyebrow + product name + the amber
 *  progress bar - so the named "checking price..." rows sit near the top
 *  of the viewport. When the concurrent photo hunt lands first, the photo
 *  joins the strip (the page starts looking real before any price). The
 *  full Hero mounts with its entrance animation once a winner exists.
 *  Typography and the banner treatment copy the Hero's own (eyebrow +
 *  tracking-tight title + white photo card) - no new design language. */
function SearchingHeroStrip({
  name,
  imageUrl,
  progress,
}: {
  name: string;
  imageUrl: string | null;
  progress: { loaded: number; failed: number; total: number; isDone: boolean };
}) {
  return (
    <section className="mt-6">
      {imageUrl ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="mb-6 flex justify-center overflow-hidden rounded-2xl bg-white p-6"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={imageUrl}
            alt={name}
            className="h-44 w-auto max-w-full object-contain sm:h-56"
          />
        </motion.div>
      ) : null}
      <p className="mb-1.5 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
        Searching for
      </p>
      <h1
        className="mb-6 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl"
        style={{ textWrap: "balance" }}
      >
        {name}
      </h1>
      <SearchProgress
        loaded={progress.loaded}
        failed={progress.failed}
        total={progress.total}
        isDone={progress.isDone}
      />
    </section>
  );
}

function UpdateGlow() {
  return (
    <motion.span
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-10 rounded-2xl sm:rounded-[14px]"
      initial={{ opacity: 0.55 }}
      animate={{ opacity: 0 }}
      transition={{ duration: 1.2, ease: "easeOut" }}
      style={{
        boxShadow:
          "0 0 0 1px rgba(246, 196, 83, 0.55), 0 0 28px rgba(246, 196, 83, 0.30), inset 0 0 22px rgba(246, 196, 83, 0.12)",
      }}
    />
  );
}

interface RetailerSlot {
  retailer_slug: string;
  product_url: string;
  state: "pending" | "ok" | "failed";
  product?: ProductPageData;
  // 2026-08-28: true for a slot seeded from the stale page's saved offers.
  // A seeded slot keeps displaying its old product through pending AND
  // failed states; only the complete event's canonical result finally
  // replaces or drops it.
  seeded?: boolean;
  // 2026-08-28: set when a VISIBLE row's product was replaced in place by a
  // fresh stream result - keys the one-shot UpdateGlow announcement.
  freshAt?: number;
  reason?: string;
}

/** B-240 step 3: identity when the switch is off (today's tree byte for byte);
 *  the folded list with the proof line when on. */
function OneCardFold({
  on,
  count,
  checkedCount,
  inStockCount,
  searching,
  names,
  children,
}: {
  on: boolean;
  count: number;
  checkedCount: number;
  inStockCount: number;
  searching: boolean;
  names: string[];
  children: ReactNode;
}) {
  if (!on) return <>{children}</>;
  return (
    <RetailerListFold count={count} checkedCount={checkedCount} inStockCount={inStockCount} searching={searching} names={names}>
      {children}
    </RetailerListFold>
  );
}

export function ComparisonStream({
  query,
  postcode,
  initialIntent,
  userId,
  forceFresh,
  committedProduct = false,
  seedPick,
  seed,
  onDeadEnd,
  attachOnly = false,
  pageSlug = null,
  oneCard = false,
}: Props) {
  const router = useRouter();
  const [intent, setIntent] = useState<ParsedIntent | undefined>(initialIntent);
  // 2026-09-19: the one-card switch as the API reports it (prop from the
  // saved page, or the stream's intent event for a fresh search).
  const [oneCardLive, setOneCardLive] = useState<boolean>(oneCard);
  useEffect(() => {
    setOneCardLive(oneCard);
  }, [oneCard]);
  const [slots, setSlots] = useState<RetailerSlot[]>(() => slotsFromSeed(seed));
  const [final, setFinal] = useState<SearchResult | null>(null);
  const [emptyReason, setEmptyReason] = useState<string | null>(null);
  const [errorState, setErrorState] = useState<{
    title: string;
    detail: string;
  } | null>(null);
  const [streamDone, setStreamDone] = useState(false);
  // 2026-09-04 Price Check on the live view: the published slug from the
  // complete event (null when the search did not publish one).
  const [publishedSlug, setPublishedSlug] = useState<string | null>(null);
  // True while we check for / navigate to a warm canonical pSEO page after
  // an empty live result (P2 cache fallback). Shows the quiet "updating"
  // state instead of flashing ComparisonNotFound before the redirect lands.
  const [cacheFallbackPending, setCacheFallbackPending] = useState(false);
  // 2026-08-28 progressive-UX: shop names from the early `checking` event -
  // the retailers the fan-out is about to query, known ~seconds in. Feeds
  // ProgressiveBuildingState until the real `targets` slots take over.
  const [earlyChecking, setEarlyChecking] = useState<string[]>([]);

  const abortRef = useRef<AbortController | null>(null);
  // Seed accessed inside the stream effect + handlers without re-triggering
  // the effect (the seed identity is tied to the mount key anyway).
  const seedRef = useRef(seed);
  seedRef.current = seed;
  const initialIntentRef = useRef(initialIntent);
  initialIntentRef.current = initialIntent;

  // Walk-away search (2026-08): background the in-flight search when the
  // shopper chooses to leave (Browse / Start another) or closes the tab, so
  // the backend finisher completes it and notifies them when it is ready.
  const streamDoneRef = useRef(false);
  // 2026-09-14: when this build started (ms). The in-place rebuild's
  // post-completion refresh waits for a page row newer than this.
  const buildStartedAtRef = useRef(Date.now() - 5_000);
  useEffect(() => {
    streamDoneRef.current = streamDone;
  }, [streamDone]);
  // 2026-09-16 (leak 3, B-234): while this search streams, the deploy
  // version check (StaleBuildWatcher) must not reload the tab. That reload
  // is what cut the ASKO search on 16 Sep; it waits for the next tick.
  useEffect(() => {
    if (streamDone) return undefined;
    beginLiveSearch();
    return endLiveSearch;
  }, [streamDone]);
  const backgroundedRef = useRef(false);
  // Email an anonymous shopper optionally leaves in the scene; threaded onto
  // every subsequent background request so a later walk-away still carries it.
  const notifyEmailRef = useRef<string | null>(null);
  // Live snapshot of this search (product + retailer count) so the header tray
  // can render an OPTIMISTIC running row the instant the shopper leaves - before
  // the server job even exists - then reconcile it with the real job.
  const searchMetaRef = useRef<{ label: string; total: number }>({
    label: query,
    total: 0,
  });
  searchMetaRef.current = {
    label: intent?.product_label || query,
    total: slots.length,
  };

  const backgroundThisSearch = useCallback(
    (opts?: { keepalive?: boolean }) => {
      if (!WALK_AWAY_SEARCH_ENABLED) return;
      // Once only, and never for a search that already finished streaming
      // (Step 4 already stamped its job ready - nothing to finish).
      if (backgroundedRef.current || streamDoneRef.current) return;
      backgroundedRef.current = true;
      void backgroundSearch(
        {
          query,
          user_postcode: postcode,
          country_code: "AU",
          notify_email: notifyEmailRef.current,
        },
        userId,
        { keepalive: opts?.keepalive ?? false },
      );
      // Tell the header Searches tray to show this search IMMEDIATELY (with an
      // optimistic row built from these details), so it appears the instant the
      // shopper leaves - not a minute later when the server job first polls in.
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("mio:search-backgrounded", {
            detail: {
              query,
              label: searchMetaRef.current.label,
              total: searchMetaRef.current.total,
            },
          }),
        );
      }
    },
    [query, postcode, userId],
  );

  // Anonymous "email me when ready": store the address and ATTACH it to the
  // search WITHOUT backgrounding (background:false) - firing the finisher now
  // would race the live SSE the shopper is still watching. If they later leave,
  // backgroundThisSearch carries the same email; if they stay, the live
  // terminal stamp emails them because notify_email is set.
  const onCaptureEmail = useCallback(
    (email: string) => {
      notifyEmailRef.current = email;
      void backgroundSearch(
        {
          query,
          user_postcode: postcode,
          country_code: "AU",
          notify_email: email,
          background: false,
        },
        userId,
      );
    },
    [query, postcode, userId],
  );

  const onWalkAwayBrowse = useCallback(() => {
    backgroundThisSearch();
    router.push("/browse" as Route);
  }, [backgroundThisSearch, router]);

  const onWalkAwayStartAnother = useCallback(() => {
    backgroundThisSearch();
    router.push("/" as Route);
  }, [backgroundThisSearch, router]);

  // Search-another from the searching overlay: background the CURRENT search
  // (so it keeps running + lands in the tray), then start the typed query -
  // same background-then-navigate contract as onWalkAwayBrowse.
  const onWalkAwaySearchAnother = useCallback(
    (q: string) => {
      const query = q.trim();
      if (!query) return;
      backgroundThisSearch();
      router.push(`/compare?q=${encodeURIComponent(query)}` as Route);
    },
    [backgroundThisSearch, router],
  );

  const walkAway = useMemo(
    () =>
      WALK_AWAY_SEARCH_ENABLED
        ? {
            onBrowse: onWalkAwayBrowse,
            onStartAnother: onWalkAwayStartAnother,
            onSearchAnother: onWalkAwaySearchAnother,
            isAnonymous: !userId,
            onCaptureEmail,
          }
        : undefined,
    [
      onWalkAwayBrowse,
      onWalkAwayStartAnother,
      onWalkAwaySearchAnother,
      userId,
      onCaptureEmail,
    ],
  );

  // Tab close (pagehide) or client-side navigation away (effect cleanup) while
  // the search is still running counts as a walk-away: fire the background
  // request with keepalive so it flushes even as the page tears down. NOT
  // visibilitychange - a tab switch keeps the SSE streaming, and backgrounding
  // then would race the still-live search. Missed pagehides (mobile) are
  // caught by the backend stuck-search sweep.
  useEffect(() => {
    if (!WALK_AWAY_SEARCH_ENABLED) return;
    const onPageHide = () => backgroundThisSearch({ keepalive: true });
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      backgroundThisSearch({ keepalive: true });
    };
    // backgroundThisSearch is stable for this mount (query/postcode/userId are
    // fixed per keyed remount); mount-once so cleanup fires only on real
    // unmount / navigation away.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2026-06-01 (Approach C - Progressive Blur-Up): the hero image is
  // LOCKED once set and never churns as cheaper retailers arrive.
  // B-290 item 3 (2026-09-28, owner-approved): it is set ONLY from a photo
  // the api's photo check confirmed as the product in a clean photo - the
  // `hero_image` message while the search runs, then the result's checked
  // picture at the end (the same one, kept by the end check). The old
  // first-shop lock and the unchecked maker's photo put another machine on
  // the Magnifica S screen; until a photo is confirmed the Hero shows
  // "Photo coming soon". Written from three sites (case "hero_image",
  // case "complete", REST fallback).
  // 2026-08-28: a seeded refresh starts with the stale page's own hero image
  // so the photo never disappears; the result's checked picture takes over
  // at the end, and the seed's photo goes only if the check rejected it.
  const lockedHeroImageRef = useRef<string | null>(seed?.imageUrl ?? null);
  // 2026-06-01: counter to force a re-render when the ref's value
  // changes (refs don't trigger re-renders by themselves). Incrementing
  // this from the same lock-write site makes the heroGroup memo
  // re-evaluate and the <img> remount with the new key + URL.
  const [lockedHeroImageTick, setLockedHeroImageTick] = useState(0);

  useEffect(() => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    const intentForThisRun = initialIntentRef.current;
    setIntent(intentForThisRun);
    // 2026-08-28: a seeded run re-seeds instead of clearing, so the stale
    // page's rows and photo survive any effect re-run; unseeded runs keep
    // the original empty reset byte-for-byte.
    setSlots(slotsFromSeed(seedRef.current));
    setFinal(null);
    setEmptyReason(null);
    setErrorState(null);
    setStreamDone(false);
    setCacheFallbackPending(false);
    setEarlyChecking([]);
    // 2026-06-01: reset the hero image lock on every new search (to the
    // seed image when seeded).
    lockedHeroImageRef.current = seedRef.current?.imageUrl ?? null;
    setLockedHeroImageTick((t) => t + 1);

    // B-290 item 3: once the result has named the page's picture, a late
    // `hero_image` message never changes it (the screen matches the page).
    let heroDecided = false;
    const settleHeroImage = (result: SearchResult | null | undefined) => {
      heroDecided = true;
      const checked = result ? checkedHeroImage(result) : null;
      const seedImg = seedRef.current?.imageUrl ?? null;
      const keepSeed =
        !checked &&
        !!seedImg &&
        lockedHeroImageRef.current === seedImg &&
        !(result ? rejectedHeroImages(result) : []).includes(seedImg);
      const next = checked ?? (keepSeed ? seedImg : null);
      if (lockedHeroImageRef.current !== next) {
        lockedHeroImageRef.current = next;
        setLockedHeroImageTick((t) => t + 1);
      }
    };

    let cancelled = false;
    let streamOpened = false;

    // 2026-06-01 (Approach C): SSE watchdog so the hero never stays
    // in its blurred / gradient placeholder forever if the stream
    // stalls without throwing. 140s wall-clock is greater than the
    // api 130s search budget; 45s idle catches the case where the
    // stream opened, returned a few results, then hung. On either
    // timeout we set streamDone+errorState so the existing
    // zero-results / error branches take over.
    let wallTimer: ReturnType<typeof setTimeout> | null = null;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    const fireWatchdog = (reason: string) => {
      if (cancelled) return;
      ac.abort();
      setErrorState({
        title: "Search took too long",
        detail:
          "The search engine stopped responding. Please try again in a moment.",
      });
      setStreamDone(true);
      // 2026-07-29 P1 fix: when the watchdog fires, the Part 1 gate below
      // shows whatever retailers already streamed in - but the slots that
      // never resolved MUST be dropped, not left rendering as empty skeleton
      // cards. Flip pending -> failed here, exactly as the `complete` event
      // does (case "complete"). sortedSlots only keeps ok + pending slots,
      // so failed ones disappear. Without this, a timed-out partial result
      // shows the real cards PLUS a stack of blank skeletons.
      setSlots((prev) =>
        prev.map((s) =>
          s.state === "pending"
            ? { ...s, state: "failed" as const, reason: "watchdog_timeout" }
            : s,
        ),
      );
      // Suppress further timeouts.
      if (wallTimer) clearTimeout(wallTimer);
      if (idleTimer) clearTimeout(idleTimer);
      void reason; // available for telemetry hooks later
    };
    const armIdleTimer = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => fireWatchdog("idle_45s"), 45_000);
    };
    // 2026-08-25: kept at 140s per the owner's direction - the fix for the
    // Logitech failure is SERVER performance, not client patience: the slow
    // fetch batch now runs OVERLAPPED with the fast one (routes/search.py
    // mio_overlapped_slow_batch, one shared fetch semaphore), collapsing the
    // worst case from two sequential 75s budgets (~190s) to roughly one
    // window (~75-100s), comfortably inside this wall. Dead streams are
    // caught by the 45s idle watchdog (keepalives reset it only while the
    // stream is alive); this wall stays the last-resort zombie guard.
    wallTimer = setTimeout(() => fireWatchdog("wall_140s"), 140_000);
    armIdleTimer();

    (async () => {
      try {
        const stream = openSearchStream(
          {
            query,
            user_postcode: postcode,
            country_code: "AU",
            intent_override: intentForThisRun,
            force_fresh: forceFresh,
            seed_pick: seedPick,
            attach_only: attachOnly,
            page_slug: pageSlug,
          },
          ac.signal,
          userId,
        );
        for await (const evt of stream) {
          if (cancelled) break;
          streamOpened = true;
          applyEvent(evt);
          // 2026-06-01: reset the 45s idle timer on every event so a
          // healthy stream that returns results within 45s of each
          // other never trips the watchdog.
          armIdleTimer();
        }
        if (!cancelled) {
          if (wallTimer) clearTimeout(wallTimer);
          if (idleTimer) clearTimeout(idleTimer);
          setStreamDone(true);
        }
      } catch (e) {
        if (cancelled || (e as Error).name === "AbortError") return;
        const failedToOpen =
          e instanceof SearchStreamError && !streamOpened;
        // 2026-09-22 (owner-caught, EarFun): a 429 is the server REFUSING the
        // search (the per-address allowance), not the search taking long.
        // Before, the open failed, the 45 s idle watchdog fired and the page
        // said "Search took too long"; or the REST fallback ran, was refused
        // too, and the page said "Server returned 429". Now: timers cleared,
        // the real wait shown, and no second request that would be refused.
        if (e instanceof SearchStreamError && e.status === 429) {
          if (wallTimer) clearTimeout(wallTimer);
          if (idleTimer) clearTimeout(idleTimer);
          setErrorState(errorForStatus(429, e.retryAfter));
          setStreamDone(true);
          return;
        }
        // 2026-09-16 (leak 3): an attach that could not open never falls
        // back to the non-streaming search (that would be a paid run).
        if (!failedToOpen || attachOnly) {
          setStreamDone(true);
          return;
        }
        try {
          const fallback = await postSearchByQuery(
            {
              query,
              user_postcode: postcode,
              country_code: "AU",
              intent_override: intentForThisRun,
              force_fresh: forceFresh,
            },
            userId,
          );
          if (cancelled) return;
          setIntent(fallback.intent);
          setFinal(fallback.result);
          setEmptyReason(fallback.empty_reason);
          // P2 cache fallback (2026-06-28): same safety net on the REST
          // fallback path as the SSE complete handler.
          {
            const liveEmpty =
              !!fallback.empty_reason ||
              !fallback.result ||
              fallback.result.groups.length === 0 ||
              (fallback.result.success_count ?? 0) === 0;
            if (liveEmpty && fallback.slug) {
              setCacheFallbackPending(true);
              void fallbackToCachedSlugIfEmpty(
                fallback.slug,
                null,
                fallback.result,
                fallback.empty_reason,
                () => cancelled,
                committedProduct,
              ).then((navigated) => {
                if (!navigated && !cancelled) setCacheFallbackPending(false);
              });
            }
          }
          // Hero-lock site #3 (REST fallback). B-290 item 3: the result's
          // checked picture only - no first-photo-found stand-in.
          settleHeroImage(fallback.result);
          setStreamDone(true);
          // Option B (2026-06-08): same redirect on the REST
          // fallback path as on the SSE complete event.
          // REST fallback path: SearchByQueryResponse only carries
          // `slug`, not page_kind. Pass null so the polling helper
          // discovers the page_kind via /api/pseo/slugs/<slug>.
          setPublishedSlug(fallback.slug ?? null);
          void maybeRedirectToCanonicalSlug(
            fallback.slug ?? null,
            null,
            fallback.empty_reason,
            postcode,
            router,
            () => cancelled,
            committedProduct,
            buildStartedAtRef.current,
            fallback.result?.fetched_at ?? null,
          );
        } catch (fbErr) {
          if (cancelled) return;
          const status = fbErr instanceof SearchError ? fbErr.status : 0;
          const detail = fbErr instanceof SearchError ? fbErr.detail : null;
          const retryAfter =
            status === 429 && detail && typeof detail === "object"
              ? Number((detail as { retry_after?: unknown }).retry_after)
              : undefined;
          if (wallTimer) clearTimeout(wallTimer);
          if (idleTimer) clearTimeout(idleTimer);
          setErrorState(errorForStatus(status, retryAfter));
          setStreamDone(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
      if (wallTimer) clearTimeout(wallTimer);
      if (idleTimer) clearTimeout(idleTimer);
    };

    function applyEvent(evt: SearchStreamEvent) {
      switch (evt.kind) {
        case "keepalive":
          // No data; the loop's armIdleTimer() reset (after this call) is
          // the whole point - it tells the idle watchdog the search is alive.
          return;
        case "intent":
          setIntent(evt.intent);
          if (evt.layout) setOneCardLive(evt.layout === "one_card");
          // Stage 1 (2026-06-28): the address bar is NO LONGER switched to
          // /c/<slug> at intent time. The intent-event slug is a pre-commit
          // placeholder written BEFORE the retailer count is known, so the
          // early replaceState stranded users on a dead /c/<slug> URL when
          // the search ended with <3 retailers (the "0 retailers"
          // dead-end that refresh/share/back could never escape). The URL
          // is now set ONLY by the complete-event redirect
          // (maybeRedirectToCanonicalSlug), which fires solely for a
          // publishable (>=3 retailer) result and never on an empty one, so
          // a thin/empty search stays on /compare and can recover.
          return;
        case "targets": {
          // 2026-05-20: dedupe by retailer_slug. Phase A discovery
          // can return multiple URLs for the same retailer (variants,
          // refurb, on-plan listings). Earlier we seeded one slot per
          // URL, which created duplicate React keys
          // (key={slot.retailer_slug}) and caused AnimatePresence to
          // leave stale CHECKING NOW cards mounted after the stream
          // resolved one URL but not the other. The downstream tier
          // groups (final.tier_groups: variant / refurbished /
          // on_plan) surface multi-URL discoveries in the "Also
          // found" section, so collapsing to one slot per retailer
          // in the main streaming grid loses no user-visible data.
          const seen = new Set<string>();
          const deduped = evt.targets.filter((t) => {
            if (seen.has(t.retailer_slug)) return false;
            seen.add(t.retailer_slug);
            return true;
          });
          // 2026-08-25 blocker fix: MERGE, never replace. The progressive
          // route emits `targets` twice (fast batch, then the slow batch's
          // additions); replacing the grid wiped every already-loaded fast
          // row and reset the page to skeletons showing only the slow
          // additions ("0 OF 2 RETAILERS" mid-build). New retailers append
          // as pending; existing slots and their loaded results stay.
          setSlots((prev) => {
            const have = new Set(prev.map((s) => s.retailer_slug));
            const additions = deduped
              .filter((t) => !have.has(t.retailer_slug))
              .map((t) => ({
                retailer_slug: t.retailer_slug,
                product_url: t.product_url,
                state: "pending" as const,
              }));
            return prev.length ? [...prev, ...additions] : additions;
          });
          return;
        }
        case "image":
          // B-290 item 3 (2026-09-28): an UNCHECKED maker's photo (older api
          // only, e.g. during a deploy). Never shown: the maker's photo now
          // arrives as `hero_image` once the check has confirmed it.
          return;
        case "hero_image":
          // B-290 item 3: a photo the check confirmed as the product in a
          // clean photo, while the search runs. First one wins (no churn); a
          // seeded refresh's own photo gives way to it; ignored once the
          // result has named the page's picture.
          if (
            !heroDecided &&
            (lockedHeroImageRef.current === null ||
              lockedHeroImageRef.current === (seedRef.current?.imageUrl ?? null))
          ) {
            lockedHeroImageRef.current = evt.image_url;
            setLockedHeroImageTick((t) => t + 1);
          }
          return;
        case "checking":
          // 2026-08-28 progressive-UX: the fan-out retailer names, known
          // seconds in. Display-only; the `targets` event stays the
          // authority on the real slot list.
          setEarlyChecking(evt.retailers.map((r) => r.name));
          return;
        case "saved_page":
          // 2026-09-10 (saved-page guard at the till): the backend found
          // a saved, servable page and spent NOTHING - navigate there.
          // Reuses the P2 cache-fallback navigator (it verifies the page
          // actually serves before moving). If verification fails, force
          // a genuine fresh search via ?r= so the shopper is never
          // dead-ended on the searching scene.
          {
            setCacheFallbackPending(true);
            void fallbackToCachedSlugIfEmpty(
              evt.slug,
              evt.page_kind,
              null,
              "saved_page",
              () => cancelled,
              committedProduct,
            ).then((navigated) => {
              if (!navigated && !cancelled) {
                setCacheFallbackPending(false);
                const u = new URL(window.location.href);
                u.searchParams.set("r", String(Date.now()));
                window.location.replace(u.toString());
              }
            });
          }
          return;
        case "result":
          setSlots((prev) => {
            // 2026-08-28: a row that was ALREADY VISIBLE with a product
            // (seeded stale offer) and just got its fresh price announces
            // the update with the one-shot glow. Brand-new rows use the
            // normal entrance animation instead.
            const existing = prev.find(
              (s) => s.retailer_slug === evt.retailer_slug,
            );
            const updatedInPlace =
              !!existing?.product && existing.state !== "ok";
            return upsertSlot(prev, {
              retailer_slug: evt.retailer_slug,
              product_url: evt.product.product_url,
              state: "ok",
              product: evt.product,
              freshAt: updatedInPlace ? Date.now() : existing?.freshAt,
            });
          });
          // B-290 item 3 (2026-09-28): the first shop's photo no longer
          // becomes the hero (it put another machine on the Magnifica S
          // screen). The api checks the photos of offers that pass the
          // card's rules and sends a confirmed one as `hero_image`.
          return;
        case "failure":
          // Mark every pending slot for this retailer as failed, not
          // just the first. Phase A can discover multiple URLs for a
          // single retailer (variants, refurb), so the targets event
          // may seed several slots with retailer_slug=X. The orchestrator
          // emits ONE failure event per retailer when its fetch path
          // gives up (captcha, cloudflare, timeout). Without this loop,
          // upsertSlot would clear only the first slot and the rest
          // would stay "CHECKING NOW" indefinitely.
          setSlots((prev) =>
            prev.map((s) =>
              s.retailer_slug === evt.retailer_slug && s.state === "pending"
                ? { ...s, state: "failed" as const, reason: evt.reason }
                : s,
            ),
          );
          return;
        case "complete":
          setFinal(evt.result);
          setEmptyReason(evt.empty_reason);
          // 2026-09-21 (owner blocker: an iPhone photo on a MacBook page) ->
          // B-290 items 3-4 (2026-09-28): the finished search names the
          // page's picture - the product in a clean photo, confirmed by the
          // photo check (the one already shown is kept) - and the screen
          // shows exactly that, so it matches the saved page. Nothing stands
          // in when none was confirmed ("Photo coming soon").
          settleHeroImage(evt.result);
          // P2 cache fallback (2026-06-28): empty live result but a warm
          // canonical pSEO page exists -> send the user there, not a dead
          // end. Hold the "updating" state while we check so
          // ComparisonNotFound never flashes.
          {
            const liveEmpty =
              !!evt.empty_reason ||
              !evt.result ||
              evt.result.groups.length === 0 ||
              (evt.result.success_count ?? 0) === 0;
            // 2026-08-28: a SEEDED refresh already sits on the cached page -
            // an empty fresh result must keep the seeded rows quietly, never
            // navigate away or flash a fallback surface.
            if (liveEmpty && evt.slug && !seedRef.current) {
              setCacheFallbackPending(true);
              void fallbackToCachedSlugIfEmpty(
                evt.slug,
                evt.page_kind,
                evt.result,
                evt.empty_reason,
                () => cancelled,
                committedProduct,
              ).then((navigated) => {
                if (!navigated && !cancelled) setCacheFallbackPending(false);
              });
            }
          }
          // GA4: search-funnel completion event (2026-06-09).
          // Captures the user-visible outcome so we can compute the
          // submit -> complete rate, lowest-TFP distribution, and
          // cache-hit-from-the-user's-POV. No PII; the query is
          // already in the URL (?q=...).
          {
            const allProducts =
              evt.result?.groups?.flatMap((g) => g.products) ?? [];
            const lowestTfp = allProducts.length
              ? allProducts.reduce((min, p) => {
                  const v = parseFloat(p.delivered_total);
                  return Number.isFinite(v) && v < min ? v : min;
                }, Number.POSITIVE_INFINITY)
              : null;
            trackSearchCompleted({
              query,
              retailerCount: evt.result?.groups?.length ?? 0,
              successCount: evt.result?.success_count ?? 0,
              lowestTfpAud:
                lowestTfp && Number.isFinite(lowestTfp) ? lowestTfp : null,
              cacheHit: Boolean(evt.result?.cache_hit),
            });
          }
          // (The 2026-06-08 back-fill that took the first photo found in the
          // result is gone - B-290 item 3: an unchecked photo never stands in.)
          // Safety net: any slot still pending when the orchestrator
          // signals complete will never resolve. Flip them to failed
          // so the placeholder cards stop saying "CHECKING NOW" once
          // the stream actually ends. The final state below (the
          // `if (final)` branch of sortedSlots) drops failed slots
          // from the canonical ordering anyway, but flipping here
          // also clears them from the mid-stream renderer in case
          // final arrives before the canonical replacement.
          setSlots((prev) =>
            prev.map((s) =>
              s.state === "pending"
                ? { ...s, state: "failed" as const, reason: "stream_complete_no_response" }
                : s,
            ),
          );
          // Option B (2026-06-08) -> B-290 item 2 (2026-09-28): once
          // the page's stored copy is confirmed as this search's, the
          // screen becomes the saved page. If it never is, the user
          // stays at /compare with the rendered results.
          setPublishedSlug(evt.slug ?? null);
          void maybeRedirectToCanonicalSlug(
            evt.slug,
            evt.page_kind,
            evt.empty_reason,
            postcode,
            router,
            () => cancelled,
            committedProduct,
            buildStartedAtRef.current,
            evt.result?.fetched_at ?? null,
          );
          return;
        case "error":
          setErrorState(errorForKind(evt.error));
          trackSearchFailed({ query, reason: evt.error });
          return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, postcode]);

  // ---- Derive sorted product list from current state ----
  //
  // Source of truth: `final` once the complete event arrives,
  // otherwise the live `slots`. Either way we end up with a single
  // ordered list of (retailerSlot, product?) pairs - the same
  // bento renders both cases. No streaming-vs-final branch in the
  // JSX below.

  // 2026-08-28 Option A dead-end valve: when a PAGE-BACKED rebuild ends with
  // nothing usable (no ok rows and an empty/failed final, or a hard error),
  // notify the parent ONCE so it restores the saved page it still holds.
  // Effect (not render) so the parent swap happens in its own commit; the
  // terminal surfaces are suppressed for page-backed runs, so nothing
  // flashes in between.
  const onDeadEndRef = useRef(onDeadEnd);
  onDeadEndRef.current = onDeadEnd;
  const deadEndFired = useRef(false);
  useEffect(() => {
    if (!seedRef.current || deadEndFired.current) return;
    if (!streamDone) return;
    const anyOk = slots.some((s) => s.state === "ok");
    const emptyFinal =
      final === null || final.groups.length === 0;
    const dead = (!anyOk && emptyFinal) || errorState !== null;
    if (dead) {
      deadEndFired.current = true;
      onDeadEndRef.current?.();
    }
  }, [streamDone, slots, final, errorState]);

  // B-117 (2026-06-18): preload-image effect MUST live above the
  // render decision tree's early returns. React requires hook order
  // to stay identical across renders; with this useEffect placed
  // below the early-return branches (errorState, ComparisonNotFound
  // empty-reason path, ComparisonNotFound success_count===0 path),
  // every empty-state render skipped the hook and triggered
  // "Rendered fewer hooks than expected" → white-screen of death on
  // /compare with empty results. The const reads `lockedHeroImageRef`
  // which is set inside the main effect, so it must be evaluated
  // every render. `void lockedHeroImageTick` keeps the memo
  // invalidation contract documented at the original site.
  void lockedHeroImageTick;
  const lockedImg = lockedHeroImageRef.current;
  useEffect(() => {
    if (!lockedImg) return;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = lockedImg;
    link.setAttribute("fetchpriority", "high");
    document.head.appendChild(link);
    return () => {
      try {
        document.head.removeChild(link);
      } catch {
        // already removed
      }
    };
  }, [lockedImg]);

  // B17: the best price on screen when the shopper leaves. Computed from the
  // final reconciled result (same basis as trackSearchCompleted's lowestTfp)
  // so the walk-away price and the completion price always agree.
  const walkAwayLowestTfp = useMemo<number | null>(() => {
    const products = final?.groups?.flatMap((g) => g.products) ?? [];
    if (products.length === 0) return null;
    const min = products.reduce((acc, p) => {
      const v = parseFloat(p.delivered_total);
      return Number.isFinite(v) && v < acc ? v : acc;
    }, Number.POSITIVE_INFINITY);
    return Number.isFinite(min) ? min : null;
  }, [final]);

  const sortedSlots = useMemo<RetailerSlot[]>(() => {
    if (final) {
      // Once complete, replace the streaming slots with the
      // canonical reconciled ordering from the server. Map each
      // canonical product back to a slot keyed by its retailer.
      // Any retailer in the original `slots` list that is NOT in
      // `final` was either failed or validator-dropped - exclude
      // it silently.
      //
      // 2026-06-07: re-sort by TFP ascending across all groups.
      // The backend reconciler returns groups in B-014 tier order
      // (delivery-confirmed groups first, then unconfirmed), and
      // splits the same product into multiple groups when a single
      // retailer publishes a different product_name + no GTIN
      // (e.g. Heinemann sending "SONY - WH1000XM5B BLACK" lands in
      // its own one-product group with delivery_confirmed=False,
      // so the B-014 sort buries it at the bottom even when its
      // TFP is below other rows in the main group). Sorting on the
      // client after the flatMap means we render strict cheapest-
      // first regardless of how the reconciler grouped, and works
      // for every retailer that gets stranded this way - not just
      // Heinemann.
      // 2026-08-21 (confirmed-price gate, Phase 1): drop offers the
      // server flagged price_confirmed=false (a bot-page / used-listing
      // outlier) before ranking, so the winner (index 0) and every
      // rendered row is a CONFIRMED price. Keeps the original list when
      // filtering would empty it. Backend gate off → every offer
      // confirmed → no-op (byte-for-byte today's behaviour).
      // B-149 (2026-08-26, owner decision): sold-out offers are hidden from
      // the page entirely - every rendered row is buyable NOW, and the crown
      // (index 0 after the sort) can never land on an out-of-stock shop.
      // Falls back to the unfiltered list when everything is sold out.
      const canonicalProducts = dropOutOfStockOffers(
        dropUnconfirmedPrices(
          final.groups.flatMap((g) => g.products).slice(),
        ),
      ).sort(
        (a, b) =>
          parseFloat(a.delivered_total) - parseFloat(b.delivered_total),
      );
      const order = new Map<string, ProductPageData>();
      for (const p of canonicalProducts) {
        order.set(p.retailer_slug, p);
      }
      // 2026-08-28 seeded refresh safety valve: a final with ZERO products
      // (empty groups / everything reconciled away) must never wipe the
      // seeded rows the visitor is looking at - fall through to the
      // slots-based path below, which keeps them.
      if (
        canonicalProducts.length > 0 ||
        !slots.some((s) => !!s.seeded && !!s.product)
      ) {
        const out: RetailerSlot[] = [];
        // First, keep canonical ordering from final.
        for (const p of canonicalProducts) {
          out.push({
            retailer_slug: p.retailer_slug,
            product_url: p.product_url,
            state: "ok",
            product: p,
          });
        }
        // 2026-09-10 (owner-ordered blocker fix, display rule): a live
        // re-check that comes back THINNER than the saved page must never
        // replace it on screen. When the fresh reconciled result has
        // fewer shops than the seed the visitor is looking at (the
        // degraded-provider signature), keep every seeded shop the fresh
        // pass did NOT cover, so the page can only get richer, never
        // thinner. The write gate already stops the thin result being
        // saved; this keeps the VIEW honest too. Healthy re-checks
        // (fresh >= seed) are unchanged - fresh fully replaces the seed.
        const seededSlots = slots.filter(
          (s): s is RetailerSlot & { product: ProductPageData } =>
            !!s.seeded && !!s.product,
        );
        if (canonicalProducts.length < seededSlots.length) {
          for (const s of seededSlots) {
            if (!order.has(s.retailer_slug)) {
              out.push({ ...s, state: "ok" });
            }
          }
          out.sort(
            (a, b) =>
              parseFloat(a.product!.delivered_total) -
              parseFloat(b.product!.delivered_total),
          );
        }
        return out;
      }
    }
    // Streaming: rank ok slots by delivered_total ascending, keep
    // pending slots at the end in their original arrival order so
    // they do not jump around mid-stream.
    const okAll = slots
      .filter((s): s is RetailerSlot & { product: ProductPageData } =>
        s.state === "ok" && !!s.product,
      )
      .slice()
      .sort(
        (a, b) =>
          parseFloat(a.product.delivered_total) -
          parseFloat(b.product.delivered_total),
      );
    // 2026-08-21 (confirmed-price gate, Phase 1): keep confirmed-price
    // slots only so a flagged bot-page/used-listing outlier never takes
    // the headline mid-stream. Fall back to the full list if filtering
    // would empty it. Backend gate off → no-op.
    // Real-deal plan Part B (2026-09-29): a row from Google's list keeps its
    // place mid-stream too (isShownPrice); read rows follow the August rule.
    const okConfirmed = okAll.filter((s) => isShownPrice(s.product));
    const okPriced = okConfirmed.length > 0 ? okConfirmed : okAll;
    // B-149: hide sold-out offers mid-stream too, so an out-of-stock shop
    // never takes the headline while the page builds. Same defensive
    // fallback: an all-sold-out stream still shows its rows.
    const okBuyable = okPriced.filter((s) => isBuyableOffer(s.product));
    const ok = okBuyable.length > 0 ? okBuyable : okPriced;
    // 2026-08-28: a SEEDED slot whose fetch failed keeps its old row on
    // screen (safety valve: a fetch failure is not proof the shop is gone);
    // the complete event's canonical result is what finally drops it.
    const pending = slots.filter(
      (s) =>
        s.state === "pending" ||
        (s.state === "failed" && !!s.seeded && !!s.product),
    );
    return [...ok, ...pending];
  }, [final, slots]);

  // ---- Render decision tree ----

  // Part 1 resilience (2026-07-29): a watchdog timeout or a mid-stream error
  // must NEVER discard retailers the user already saw. If at least one usable
  // result has streamed in (an "ok" slot, or a reconciled final with groups),
  // fall through to the normal comparison render and show what we have; only
  // the hard "Search took too long" screen when there is genuinely nothing to
  // show. This is what turns the reported "saw retailers, then error" failure
  // into "saw retailers, they stay". The SearchAgainBar (always rendered) is
  // the path to a full re-run.
  const hasStreamedResults =
    slots.some((s) => s.state === "ok") ||
    (final !== null && final.groups.length > 0);
  // 2026-08-28 seeded refresh: seeded ROWS on screen - no terminal surface
  // may replace them. (Option A passes no rows, so this stays false; kept
  // for the dormant row-seeding machinery.)
  const hasSeed = slots.some((s) => !!s.seeded && !!s.product);
  // 2026-08-28 Option A: a PAGE-BACKED run (stale /c rebuild - a saved page
  // exists behind this build). Terminal surfaces are suppressed; the
  // dead-end effect above notifies ProductLiveBuild, which restores the
  // saved page. A rebuild can therefore never end on "couldn't find it"
  // for a product we have a page for.
  const pageBacked = !!seed;

  if (errorState && !hasStreamedResults && !hasSeed && !pageBacked) {
    return <ErrorState title={errorState.title} detail={errorState.detail} />;
  }

  // B-098 search wall: the api returned a terminal wall reason (set in
  // emptyReason) instead of results. Render the matching card BEFORE the
  // generic empty / not-found branches so a wall never reads as "no
  // results". The wall carries slug=null so no cache-fallback nav fires.
  // Seeded / page-backed refresh: a walled refresh keeps the saved page.
  if (!hasSeed && !pageBacked) {
    if (emptyReason === "wall_signup_required") return <SignupWallCard />;
    if (emptyReason === "wall_daily_limit") return <DailyLimitCard />;
    if (emptyReason === "wall_at_capacity") return <AtCapacityCard />;
    // Geo gate: visitor outside Australia tried a fresh search.
    if (emptyReason === "geo_blocked_non_au") return <GeoBlockCard />;
  }

  // P2 cache fallback: while we check for / navigate to a warm canonical
  // page after an empty live result, show the quiet "updating" surface
  // rather than flashing the 0-result ComparisonNotFound.
  if (cacheFallbackPending) {
    return (
      <ComparisonUpdating
        query={query}
        productLabel={intent?.product_label ?? undefined}
      />
    );
  }

  if (
    streamDone &&
    final === null &&
    emptyReason !== null &&
    !hasSeed &&
    !pageBacked
  ) {
    return (
      <ComparisonNotFound
        query={query}
        productLabel={intent?.product_label ?? undefined}
        reasonHint={emptyMessageFor(
          emptyReason, query, intent?.product_label ?? "",
        )}
      />
    );
  }

  if (
    streamDone &&
    !hasSeed &&
    !pageBacked &&
    sortedSlots.length === 0 &&
    (final !== null || slots.length > 0)
  ) {
    // B-117 (2026-06-18): graceful no-results surface. Replaces the
    // older NoResultsHonest block — same data, premium copy and a
    // softer-query suggestion + popular-search chips so the user has
    // somewhere to go without bouncing back to the homepage.
    //
    // 2026-06-28 fix: removed an extra `slots.filter(ok).length === 0`
    // guard. When a retailer STREAMED a result (creating an "ok" slot)
    // but the final reconcile DROPPED it to 0 usable results, that stale
    // "ok" slot blocked this honest-empty branch, so the empty product
    // layout ("0 retailers" + skeletons) rendered instead of the honest
    // no-results page. `sortedSlots` is derived from the canonical
    // reconciled result (final.groups) when `final` is set, so
    // `sortedSlots.length === 0` already and correctly means "nothing to
    // show"; the raw-slot check was redundant AND caused the blank-page
    // dead-end on streamed-then-reconciled-away searches.
    const failureSlots = slots.filter((s) => s.state === "failed");
    const attempts = failureSlots.length || slots.length;
    return (
      <ComparisonNotFound
        query={query}
        productLabel={intent?.product_label ?? undefined}
        attempts={attempts}
      />
    );
  }

  // ---- Unified BAU layout (loading + streaming + complete share one tree) ----

  // 2026-08-28 seeded refresh: before any fresh result lands, the winner
  // display falls back to the stale page's own (already crown-ruled) top
  // offer, so the Hero never degrades to a skeleton on a page that was
  // fully rendered a moment ago. First fresh ok result takes over.
  const cheapestSlotByPrice =
    sortedSlots.find((s) => s.state === "ok" && !!s.product) ??
    sortedSlots.find((s) => !!s.seeded && !!s.product);
  // B-240 step 3: with the switch on, the card goes to the cheapest offer
  // that passes the winner checks (lib/oneCard.ts); nothing passes = cheapest.
  const cheapestSlot = oneCardLive
    ? (sortedSlots.find(
        (s) => s.state === "ok" && !!s.product && passesWinnerChecks(s.product),
      ) ?? cheapestSlotByPrice)
    : cheapestSlotByPrice;
  const cheapestProduct = cheapestSlot?.product;
  const otherSlotsByPrice = cheapestSlot
    ? sortedSlots.filter((s) => s !== cheapestSlot)
    : sortedSlots;
  // Same-product check (3 Oct 2026): with the one card on, a shop whose row
  // is not the page's product goes to the bottom of the list, as on the
  // saved page (lib/oneCard.ts sameProductLast). Nothing is removed.
  const otherSlots = oneCardLive
    ? sameProductLast(otherSlotsByPrice, (s) => s.product)
    : otherSlotsByPrice;
  // B-149: sortedSlots is already buyable-only (sold-out hidden above), so
  // okProducts - the negotiation panel, list layout, and summary inputs -
  // inherits the same rule with no second filter.
  const okProductsByPrice = dropCarrierOffers(
    sortedSlots
      .filter((s): s is RetailerSlot & { product: ProductPageData } =>
        s.state === "ok" && !!s.product,
      )
      .map((s) => s.product),
  );
  // B-240 step 3: same reorder as the saved page, so every consumer that
  // reads [0] as the winner (Hero, AskPanel, PriceBeatCallout) agrees.
  const oneCardPick = oneCardLive ? pickOneCard(okProductsByPrice) : null;
  const okProducts = oneCardPick ? oneCardPick.ordered : okProductsByPrice;
  // 2026-08-28 seeded refresh: until fresh results exist, the Hero's winner
  // block renders the stale page's own offers (the exact list /c was
  // showing). Action panels keep okProducts (fresh-only) - actions should
  // bind to confirmed-fresh data first.
  const seededProducts = dropCarrierOffers(
    sortedSlots
      .filter((s): s is RetailerSlot & { product: ProductPageData } =>
        !!s.seeded && !!s.product && s.state !== "ok",
      )
      .map((s) => s.product),
  );
  const displayProducts = okProducts.length > 0 ? okProducts : seededProducts;

  // Hero group - same shape in loading vs loaded modes. The Hero
  // component itself reads `loadingState` and decides what to
  // render. We update the group's `products` array in place as
  // retailers arrive; the hero's mount never changes.
  const heroGroup: EquivalenceGroup =
    final && final.groups.length > 0
      ? {
          ...final.groups[0],
          canonical_name:
            intent?.product_label || final.groups[0].canonical_name,
          // B-290 item 3: the checked photo only (no group photo stand-in).
          image_url: lockedImg,
          // displayProducts === okProducts whenever fresh results exist;
          // the seeded fallback only matters pre-first-result.
          products: displayProducts,
        }
      : {
          canonical_name:
            intent?.product_label || intent?.raw_query || query,
          canonical_brand: intent?.brand ?? null,
          canonical_gtin: null,
          canonical_mpn: intent?.model_number ?? null,
          image_url: lockedImg,
          products: displayProducts,
          delivery_confirmed: okProducts.length > 0,
        };

  // Progressive search (2026-08-19): while building, never render the raw
  // empty-box loading skeletons. Show only LOADED rows + the clean
  // ProgressiveBuildingState in place of the shops not yet resolved.
  const buildingActive = PROGRESSIVE_SEARCH_ENABLED && !streamDone;
  // 2026-08-28 seeded refresh: seeded rows (old data + CheckingChip) stay
  // visible while building - only BARE pending slots (no product yet) are
  // deferred to ProgressiveBuildingState.
  const displayOtherSlots = buildingActive
    ? otherSlots.filter(
        (s) => (s.state === "ok" && !!s.product) || (!!s.seeded && !!s.product),
      )
    : otherSlots;
  const pendingSlotsList = slots.filter((s) => s.state === "pending");
  const pendingCount = pendingSlotsList.length;
  // Fix 1 (2026-08-19): the discovered shops are known at the `targets` event
  // (~6s), long before their prices. Surface their REAL names now so the shopper
  // watches actual shops being checked, not a blank screen. Cap the visible list.
  // 2026-08-28: seeded slots display as real rows above, so only BARE pending
  // slots feed the building rows; before `targets` lands, the early `checking`
  // event's fan-out names fill the same rows (~5s instead of ~30s+ cold).
  const barePendingNames = pendingSlotsList
    .filter((s) => !s.product)
    .slice(0, 8)
    .map((s) => prettifyRetailerSlug(s.retailer_slug));
  const pendingShops =
    barePendingNames.length > 0 ? barePendingNames : earlyChecking.slice(0, 8);
  // Show the named building rows only when there is genuinely unnamed work in
  // flight: bare pending slots, or the pre-targets window of an UNSEEDED run
  // (a seeded page's rows already carry the checking state themselves).
  const showBuildingRows =
    buildingActive &&
    (barePendingNames.length > 0 || (!hasSeed && slots.length === 0));
  const buildingLabel =
    slots.length === 0
      ? `Finding Australian retailers that stock ${heroGroup.canonical_name}…`
      : pendingCount > 0
        ? `Checking prices across ${pendingCount} ${
            pendingCount === 1 ? "retailer" : "retailers"
          }…`
        : "Putting your comparison together…";

  // Watch targets for the AskPanel's "Watch the price" toggle.
  //
  // Primary source: the live per-retailer slots. On a cache-MISS search these
  // are populated by the targets/result events, so this is non-empty and the
  // Watch toggle shows.
  //
  // 2026-07-29 fix: on a cache-HIT the stream emits ONE `complete` event with
  // no targets/result events (see the case "complete" note above), so `slots`
  // stays empty (or all-failed), which left watchTargets empty and HID the
  // Watch toggle on cache-hit searches even though the comparison rendered
  // fine from `final`. Fall back to the SAME source the render uses - the
  // final result's products - so Watch is present on every search that
  // returned at least one retailer, not just cache-miss ones. Mirrors the
  // /c/<slug> page, which builds its targets from allProducts.
  const _slotWatchTargets = slots
    .filter((s) => s.state !== "failed")
    .map((s) => ({
      retailer_slug: s.retailer_slug,
      product_url: s.product_url,
      fetch_adapter: "json_ld_universal",
      scraping_tier: 1,
      country_code: "AU",
    }));
  const watchTargets =
    _slotWatchTargets.length > 0
      ? _slotWatchTargets
      : (final?.groups.flatMap((g) => g.products) ?? []).map((p) => ({
          retailer_slug: p.retailer_slug,
          product_url: p.product_url,
          fetch_adapter: "json_ld_universal",
          scraping_tier: 1,
          country_code: "AU",
        }));

  // Total successes across both bands. Banner shows when total
  // resolved < 5 AND streamDone.
  const totalSuccessCount = final?.success_count ?? okProducts.length;
  const thinResult =
    streamDone && totalSuccessCount > 0 && totalSuccessCount < 5;
  // Header summary: retailers checked (fan-out) vs those actually in stock.
  // in-stock counts only rows that are not out of stock, so the copy is never
  // false. Falls back to the plain count when nothing was narrowed.
  const totalTargetCount = final?.target_count ?? slots.length;
  const totalInStockCount = okProducts.filter(
    (p) => p.stock_status !== "out_of_stock",
  ).length;
  // 2026-10-01 (owner): the one-card sentence says "Checking" only while the
  // progress strip above it still counts a retailer as loading. Same rule as
  // SearchProgress (done = ok + failed, pending = total - done), so the strip
  // ("All retailers in") and the card never disagree.
  const allRetailersIn =
    slots.length > 0 &&
    slots.filter((s) => s.state === "ok" || s.state === "failed").length >= slots.length;

  // pSEO Phase 3 (2026-06-12): if the parsed intent is a list intent
  // (no brand AND no model_number), render the same layout the
  // published /c/list/<slug> page uses. Without this branch the
  // streaming surface ALWAYS showed the product-style Hero plus
  // retailer rows, even when the URL had already been replaced to
  // /c/list/<slug>. A reload of the same URL then showed a totally
  // different layout - same URL, two different layouts. Bug
  // discovered 2026-06-12 from a user screenshot pair.
  // 2026-08-16 never-list contract: a committed product tap NEVER renders the
  // list layout, even when its generic title parses to a list intent - it must
  // show that product's comparison (a list layout here is the same loop).
  if (!committedProduct && isListIntent(intent)) {
    const listLabel =
      intent?.product_label || intent?.raw_query || query;
    // 2026-06-13: sanitise constraints to strip parens / slashes /
    // filler tokens before rendering. Defends against the LLM
    // intent parser leaking marketing fluff like
    // "best (top-rated / best value)". See web/lib/list-entries.ts.
    const cleanConstraints = sanitiseConstraints(intent?.constraints);
    const constraintTail =
      cleanConstraints.length > 0
        ? ` (${cleanConstraints.join(", ")})`
        : "";
    const heading = `Best ${listLabel}${constraintTail}`;
    // Derive list entries from final.groups when the complete event
    // has landed (richest source: equivalence groups dedup across
    // retailers). Otherwise fall back to a synthesised single-group
    // shape built from the streaming okProducts so the user sees
    // products as they arrive instead of an empty page.
    const sourceGroups: EquivalenceGroup[] = final
      ? final.groups
      : okProducts.length > 0
        ? [
            {
              canonical_name: listLabel,
              canonical_brand: null,
              canonical_gtin: null,
              canonical_mpn: null,
              image_url: lockedImg,
              products: okProducts,
            } as EquivalenceGroup,
          ]
        : [];
    const entries = buildListCardEntries(sourceGroups, 10);
    // 2026-06-12 (list-streaming design pass): editorial copy for the
    // two visible states (loading + complete). Loading copy is generic
    // and honest about what the engine is doing; complete copy summarises
    // what the user is looking at. Live-search has no LLM-generated
    // editorial paragraph available synchronously (the publisher creates
    // it in the background, AFTER complete), so we use hard-coded copy
    // that matches the published mio-list-streaming-mockup.html.
    const editorialLoading =
      "Ranked by what you actually pay at checkout, not the sticker price on the shelf. Hang on while we read the product pages and add delivery, cashback, and loyalty rewards.";
    const editorialComplete =
      entries.length > 0
        ? `${entries.length} ${entries.length === 1 ? "product" : "products"} ranked by what you actually pay at checkout. Cheapest deal at the top, all sold by Australian retailers.`
        : "";
    // Skeleton padding: while the stream is in flight, pad the visible
    // cards out to SKELETON_TARGET_ROWS so the page always carries
    // visible mass instead of empty space below the thinking pill.
    // Bound by 6 rows (mockup-locked count) so the page does not jump
    // when the real 7th+ card arrives.
    const SKELETON_TARGET_ROWS = 6;
    const skeletonRowsToRender = !streamDone
      ? Math.max(0, SKELETON_TARGET_ROWS - entries.length)
      : 0;
    return (
      <main className="cmp-surface relative mx-auto min-h-screen max-w-3xl bg-background px-5 pb-10 sm:px-8 sm:pb-14">
        <SearchAgainBar initialQuery={query} />
        {SEARCH_SCENE_ENABLED && !PROGRESSIVE_SEARCH_ENABLED ? (
          <SearchingSceneOverlay
            loaded={slots.filter((s) => s.state === "ok").length}
            failed={slots.filter((s) => s.state === "failed").length}
            total={slots.length}
            isDone={streamDone}
            walkAway={walkAway}
          />
        ) : null}
        <header className="mt-6">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
            {heading}
          </h1>
          <p
            className={
              "mt-3 text-sm leading-relaxed " +
              (streamDone ? "text-muted-foreground" : "text-muted-foreground")
            }
          >
            {streamDone ? editorialComplete : editorialLoading}
          </p>
        </header>

        {entries.length === 0 && streamDone ? (
          <div className="mt-8 rounded-2xl border border-border bg-secondary p-6 text-sm text-muted-foreground">
            We could not find enough products to build a useful list for this
            query. Try a more specific category or constraint.
          </div>
        ) : (
          <section className="mt-6 space-y-3">
            {entries.map((entry) => (
              <ListCard key={entry.brand + entry.canonical_name} entry={entry} />
            ))}
            {Array.from({ length: skeletonRowsToRender }, (_, i) => (
              <ListCardSkeleton
                key={`skeleton-${i}`}
                position={entries.length + i + 1}
              />
            ))}
          </section>
        )}

        {/* 2026-08-28 (owner): "Still checking prices" strip removed here too
            (list-building branch) - same affordance-only rationale as the
            product branch below. */}

        {final ? (
          <Disclosures
            postcode={postcode}
            fetchedAt={final.fetched_at}
            cacheHit={final.cache_hit}
          />
        ) : null}
      </main>
    );
  }

  return (
    <BreakdownRevealContext.Provider value={PROGRESSIVE_SEARCH_ENABLED}>
    <main className="cmp-surface relative mx-auto min-h-screen max-w-4xl bg-background px-5 pb-10 sm:px-8 sm:pb-14">
      {/* 2026-06-01: TurnstileGate removed. Invisible mode produced
          silent failures (no token → 403 missing_token) hitting real
          users. Pre-launch B-098 sign-up wall after 10 anon searches
          replaces it as the primary anti-abuse layer. */}
      {/* 2026-06-06 (Option A): sticky frosted search-again bar.
          Always-visible affordance for re-searching from /compare.
          Prefilled with current query; submit routes to
          /compare?q=<new query> (no from_chip, so clarify re-runs). */}
      <SearchAgainBar initialQuery={query} />
      {/* B17: records the shopper who reads this comparison and leaves
          without acting, with the best price they saw. Only fires once the
          stream produced retailers, so an in-flight or empty search is never
          counted as a walk-away. Renders nothing. */}
      <WalkAwayTracker
        productSlug={final?.cache_key ?? null}
        pageKind="compare_streaming"
        retailerCount={final?.success_count ?? null}
        lowestTfpAud={walkAwayLowestTfp}
      />
      {SEARCH_SCENE_ENABLED && !PROGRESSIVE_SEARCH_ENABLED ? (
        <SearchingSceneOverlay
          loaded={slots.filter((s) => s.state === "ok").length}
          failed={slots.filter((s) => s.state === "failed").length}
          total={slots.length}
          isDone={streamDone}
          walkAway={walkAway}
        />
      ) : null}
      {/* Hero is ONE persistent mount. Its `loadingState` prop
          flips from "loading" to "loaded" when the first retailer
          arrives - the `<motion.section>` inside Hero stays
          mounted, so the entrance animation fires exactly once
          for the page lifetime. No blink.

          2026-08-28 slim searching hero (flag-gated rollback lever):
          while a fresh search has neither a photo nor a priced winner,
          the dead hero skeleton is replaced by the slim strip so the
          named checking rows sit at the top. Seeded refreshes always
          have a winner (cheapestProduct) so they keep the full Hero.
          Flag OFF: today's tree byte-for-byte. */}
      <SubmittedClaimsProvider>
      {SLIM_SEARCH_HERO_ENABLED &&
      buildingActive &&
      !cheapestProduct &&
      !hasSeed ? (
        <SearchingHeroStrip
          name={heroGroup.canonical_name}
          imageUrl={lockedImg}
          progress={{
            loaded: slots.filter((s) => s.state === "ok").length,
            failed: slots.filter((s) => s.state === "failed").length,
            total: slots.length,
            isDone: streamDone,
          }}
        />
      ) : (
      <Hero
        group={heroGroup}
        postcode={postcode}
        watchTargets={watchTargets}
        loadingState={cheapestProduct ? "loaded" : "loading"}
        progress={{
          loaded: slots.filter((s) => s.state === "ok").length,
          failed: slots.filter((s) => s.state === "failed").length,
          total: slots.length,
          isDone: streamDone,
        }}
        collapseEmptyBanner={PROGRESSIVE_SEARCH_ENABLED}
        checkedImagesOnly
        rejectedImages={final ? rejectedHeroImages(final) : null}
      />
      )}

      {/* 2026-09-04: same Price Check strip as the /c page, fetched once
          the search completes and its page is published. Renders nothing
          when no slug was published or no history exists. */}
      <LivePriceCheck slug={publishedSlug} active={streamDone} />
      {oneCardPick?.cheaperWithCatch && oneCardPick.catchReason ? (
        <CheaperElsewhereStrip
          offer={oneCardPick.cheaperWithCatch}
          reason={oneCardPick.catchReason}
        />
      ) : null}

      {BROADCAST_ENABLED ? (
        <AskPanel
          allRows={okProducts}
          watchTargets={watchTargets}
          postcode={postcode}
          pageSlug={publishedSlug}
        />
      ) : null}

      {thinResult && (
        <div className="mt-2 mb-6 rounded-xl border border-border/60 bg-secondary/30 backdrop-blur-md p-4 text-sm">
          <p className="font-medium">
            Limited results.{" "}
            <span className="font-normal text-muted-foreground">
              We found {totalSuccessCount}{" "}
              {totalSuccessCount === 1 ? "retailer" : "retailers"} carrying
              this. Some products are rare or only stocked at a few stores.
              The prices below are real and ranked by True Final Price.
            </span>
          </p>
        </div>
      )}

      {/* v2 (2026-05-27): rank-0 visual is owned by v2/Hero (merged
          winner block). v2/ComparisonRow returns null for
          tier="winner" so the rank-0 retailer does not render twice. */}
      <section className="mt-2">
        <ErrorBoundary
          label={`ComparisonRow ${cheapestSlot?.retailer_slug ?? "placeholder"}`}
        >
          <ComparisonRow
            product={cheapestSlot?.product}
            rank={0}
            competitor={null}
            allRows={okProducts}
            loadingState={cheapestSlot?.product ? "loaded" : "loading"}
            retailerSlug={cheapestSlot?.retailer_slug}
            tier="winner"
          />
        </ErrorBoundary>
      </section>

      {/* 2026-09-10 (owner-approved Option B): the best-possible-price
          callout sits at PAGE level between the winner card and the
          list - Octopusden's own market insight, never a competitor's
          pitch inside the winner's card (retailer-relationship
          decision, investigated 2026-09-10). Renders only when a
          registered price-beat policy produces a price STRICTLY below
          the winner's True Final Price; ranking never changes.
          2026-09-10 fix (owner-caught in local test): reads
          displayProducts - the SAME array the rows render from - not
          okProducts, which is empty on a seeded /c page with no live
          rebuild (the main surface for this feature). */}
      {displayProducts.length > 0 ? (
        <PriceBeatCallout rows={displayProducts} />
      ) : null}

      {/* Other retailers - persistent retailer-keyed slots.
          B-P0-09 v3 (2026-05-15): at T=0 (before the `targets`
          event arrives, slots=[]) the bento renders 6 anonymous
          placeholder cards keyed by `placeholder-${i}`. This
          matches loading.tsx's ComparisonShell exactly, so the
          loading.tsx -> ComparePage React tree swap is visually
          a no-op: same Hero, same progress bar, same 6
          placeholder cards. When the real `targets` event lands,
          AnimatePresence cross-fades the placeholders out and
          the retailer-named slots in.

          `initial={false}` on AnimatePresence skips the entrance
          animation on first render (avoids per-card fade-in on
          route transition) but still animates subsequent enters
          and exits.

          B-IMG-01 follow-up (2026-05-27): the "Other retailers" /
          "Ranked by TFP" header was replaced with the retailer count
          + "Sorted by True Final Price" line per the signed redesign,
          matching the /c/[slug] static page. */}
      <OneCardFold
        on={oneCardLive}
        count={displayOtherSlots.length}
        checkedCount={totalTargetCount}
        inStockCount={totalInStockCount}
        searching={!streamDone && !allRetailersIn}
        names={displayOtherSlots.slice(0, 6).map((s) => prettifyRetailerSlug(s.retailer_slug))}
      >
      <section className="mt-4">
        {/* 2026-05-31 mockup audit:
              .desk-rowcount font-size 13px / .rowcount 12px mobile;
              .caps font-size 11px desktop / 10px mobile;
              .caps letter-spacing 0.12em;
              .caps color var(--cmp-fg-3) (no opacity reduction). */}
        {/* 2026-08-28: gate on FRESH results (okProducts), not the seeded
            display fallback - a seeded refresh must not read "0 have it in
            stock" while its first fresh price is still in flight; the
            progress strip owns the numbers until then. */}
        {!oneCardLive && (okProducts.length > 0 || !buildingActive) ? (
        <div className="mb-5 flex flex-col items-center gap-1.5 text-center text-[12px] text-muted-foreground sm:text-[13px]">
          {/* 2026-05-27: the " · N couldn't be reached" fragment was
              removed by user direction. The data is preserved server-
              side (analytics events + SearchResult.unreachable +
              comparison_pages_cache.rows) for ops use; only the user-
              facing display is gone. */}
          <p>
            {totalTargetCount > totalSuccessCount ? (
              <>
                Checked {totalTargetCount} retailers &middot; {totalInStockCount}{" "}
                {totalInStockCount === 1 ? "has" : "have"} it in stock
              </>
            ) : (
              <>
                {totalSuccessCount}{" "}
                {totalSuccessCount === 1 ? "retailer" : "retailers"}
              </>
            )}
          </p>
          <p className="text-[12px] uppercase tracking-[0.12em] text-muted-foreground sm:text-[12px]">
            Sorted by True Final Price
          </p>
        </div>
        ) : null}
        {/* 2026-05-27 (rev 2) → 2026-05-31 refinement: compact tier
            now full-width one-per-row per the locked mockup, instead
            of the previous sm:grid-cols-2 layout. Expanded tier
            unchanged (single-column stack at full width). Pre-
            targets placeholders also full-width so the loading → loaded
            transition has no layout shift. */}
        {displayOtherSlots.length > 0 ? (
          <>
            {displayOtherSlots.slice(0, 2).length > 0 ? (
              <div className="space-y-3">
                <AnimatePresence mode="popLayout" initial={false}>
                  {displayOtherSlots.slice(0, 2).map((slot, sliceIdx) => {
                    const idx = sliceIdx;
                    const competitor = slot.product
                      ? okProducts.find(
                          (p) =>
                            p.retailer_slug !== slot.product!.retailer_slug &&
                            parseFloat(p.delivered_total) <
                              parseFloat(slot.product!.delivered_total),
                        ) ?? null
                      : null;
                    return (
                      <motion.div
                        key={slot.retailer_slug}
                        layout
                        className="relative"
                        {...(oneCardLive ? { id: `retailer-${slot.retailer_slug}` } : {})}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.97 }}
                        transition={{
                          duration: 0.25,
                          ease: [0.16, 1, 0.3, 1],
                        }}
                      >
                        {/* 2026-08-28: one-shot glow when this visible row's
                            fresh price just landed (keyed so it replays only
                            on a NEW update). */}
                        {slot.freshAt ? <UpdateGlow key={slot.freshAt} /> : null}
                        <ErrorBoundary
                          label={`ComparisonRow ${slot.retailer_slug}`}
                        >
                          <ComparisonRow
                            product={slot.product}
                            rank={idx + 1}
                            competitor={competitor}
                            allRows={okProducts}
                            loadingState={slot.product ? "loaded" : "loading"}
                            retailerSlug={slot.retailer_slug}
                            tier="expanded"
                            postcode={postcode}
                          />
                        </ErrorBoundary>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            ) : null}
            {displayOtherSlots.slice(2).length > 0 ? (
              <div className="mt-3 space-y-3">
                <AnimatePresence mode="popLayout" initial={false}>
                  {displayOtherSlots.slice(2).map((slot, sliceIdx) => {
                    const idx = sliceIdx + 2;
                    const competitor = slot.product
                      ? okProducts.find(
                          (p) =>
                            p.retailer_slug !== slot.product!.retailer_slug &&
                            parseFloat(p.delivered_total) <
                              parseFloat(slot.product!.delivered_total),
                        ) ?? null
                      : null;
                    return (
                      <motion.div
                        key={slot.retailer_slug}
                        layout
                        className="relative"
                        {...(oneCardLive ? { id: `retailer-${slot.retailer_slug}` } : {})}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.97 }}
                        transition={{
                          duration: 0.25,
                          ease: [0.16, 1, 0.3, 1],
                        }}
                      >
                        {/* 2026-08-28: one-shot glow when this visible row's
                            fresh price just landed (keyed so it replays only
                            on a NEW update). */}
                        {slot.freshAt ? <UpdateGlow key={slot.freshAt} /> : null}
                        <ErrorBoundary
                          label={`ComparisonRow ${slot.retailer_slug}`}
                        >
                          <ComparisonRow
                            product={slot.product}
                            rank={idx + 1}
                            competitor={competitor}
                            allRows={okProducts}
                            loadingState={slot.product ? "loaded" : "loading"}
                            retailerSlug={slot.retailer_slug}
                            tier="compact"
                          />
                        </ErrorBoundary>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            ) : null}
          </>
        ) : slots.length === 0 && !streamDone && !final && !PROGRESSIVE_SEARCH_ENABLED ? (
          /* Pre-targets placeholders - 2026-05-31: single-column
             to match the new compact-tier full-width layout.
             ComparisonShell also switched to single column so the
             route transition stays seamless.

             2026-08-16 (frozen-skeleton fix): gated on !streamDone && !final.
             These 6 placeholders are the T=0 loading state, shown before the
             `targets` event seeds `slots`. But a comparison-cache HIT completes
             with only intent+complete (no `targets` event), so `slots` stays []
             permanently; and when the result reconciles to a single retailer the
             winner goes to the Hero, leaving `otherSlots` empty too. Without this
             guard the branch matched that TERMINAL state and rendered 6 skeletons
             that never resolve. Once the stream is done / `final` is set we render
             nothing here - the Hero and the "Checked N / M in stock" line above
             are the complete answer for a single-retailer result. */
          <div className="space-y-3">
            <AnimatePresence mode="popLayout" initial={false}>
              {Array.from({ length: 6 }).map((_, i) => (
                <motion.div
                  key={`placeholder-${i}`}
                  layout
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{
                    duration: 0.25,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                >
                  {/* 2026-06-01 (Approach C, Step 4): pre-targets
                      placeholders pass the correct tier so their
                      skeleton geometry matches the eventual loaded
                      card. Ranks 1-2 are expanded; the rest are
                      compact. Without this every slot rendered as
                      compact, then jumped to expanded on the first
                      two when targets arrived. */}
                  <ComparisonRow
                    rank={i + 1}
                    loadingState="loading"
                    tier={i < 2 ? "expanded" : "compact"}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : null}
        {showBuildingRows ? (
          <ProgressiveBuildingState label={buildingLabel} shops={pendingShops} />
        ) : null}
      </section>
      </OneCardFold>

      {/* Also found: hidden 2026-09-15 until designed (owner). */}
      </SubmittedClaimsProvider>

      {/* 2026-08-28 (owner): the "Still checking prices - keep watching" strip
          removed. It was affordance-only (its background-on-leave logic lives
          in this component's pagehide/cleanup effects, untouched); its
          anonymous email field is dead in prod behind the signed-in search
          wall, and signed-in shoppers are notified automatically. */}

      {final && (
        <Disclosures
          postcode={postcode}
          fetchedAt={final.fetched_at}
          cacheHit={final.cache_hit}
        />
      )}
    </main>
    </BreakdownRevealContext.Provider>
  );
}

// ---------- helpers ----------

function upsertSlot(
  prev: RetailerSlot[],
  next: RetailerSlot,
): RetailerSlot[] {
  const idx = prev.findIndex(
    (s) => s.retailer_slug === next.retailer_slug,
  );
  if (idx < 0) {
    return [...prev, next];
  }
  const copy = prev.slice();
  copy[idx] = { ...prev[idx], ...next };
  return copy;
}

function errorForStatus(
  status: number,
  retryAfterSeconds?: number,
): { title: string; detail: string } {
  if (status === 429) {
    // The server's per-address search allowance (30 an hour, 100 a day).
    // Say so, with the real wait from the response, instead of a bare code.
    const secs =
      Number.isFinite(retryAfterSeconds) && (retryAfterSeconds as number) > 0
        ? Math.ceil(retryAfterSeconds as number)
        : null;
    const wait =
      secs === null
        ? "in a little while"
        : secs < 90
          ? "in about a minute"
          : `in about ${Math.ceil(secs / 60)} minutes`;
    return {
      title: "Search limit reached",
      detail: `You have used this hour's searches from your connection. You can search again ${wait}.`,
    };
  }
  if (status === 503) {
    return {
      title: "Search is busy right now",
      detail:
        "One of our upstream services was slow to respond. Please try again in a moment.",
    };
  }
  if (status === 0 || status === -1) {
    return {
      title: "Couldn't reach the search engine",
      detail:
        "We had a network hiccup talking to our backend. Please try again in a moment.",
    };
  }
  return {
    title: "Search couldn't complete",
    detail: `Server returned ${status}. Please try again in a moment.`,
  };
}

function errorForKind(kind: string): { title: string; detail: string } {
  switch (kind) {
    case "cost_budget_exceeded":
      return {
        title: "This search is unusually expensive",
        detail:
          "We hit our per-search cost ceiling before all retailers responded. " +
          "Try a more specific brand + model query, which is usually cheaper.",
      };
    case "upstream_unavailable":
      return {
        title: "Upstream service hiccup",
        detail:
          "An upstream service (proxy, SERP, or LLM) didn't respond in time. " +
          "Please try again in a moment.",
      };
    default:
      return {
        title: "Search couldn't complete",
        detail: "Something went wrong on our side. Please try again in a moment.",
      };
  }
}

// B-P0-22 (2026-05-17): out-of-scope copy. Each category gets a
// specific, calm explanation + a suggestion of what Mio IS for. Keeps
// the tone honest and brand-confident; user reads "Mio doesn't compare
// X yet" as a deliberate boundary, not an error. Keys match the API
// constants in app/services/intent_parser.py::OutOfScopeCategory.
const OUT_OF_SCOPE_COPY: Record<string, string> = {
  vehicle:
    "Octopusden doesn't compare cars, motorcycles, or other vehicles yet. We focus on consumer products you buy and ship home. Try things like phones, TVs, laptops, headphones, or appliances.",
  real_estate:
    "Octopusden doesn't compare property listings or real estate. We focus on consumer products you buy and ship home.",
  professional_service:
    "Octopusden doesn't compare services like haircuts, plumbing, or legal advice. We focus on consumer products you buy and ship home. (Tools used by professionals — e.g. clippers, stud finders — are in scope.)",
  b2b_equipment:
    "Octopusden doesn't compare commercial or industrial equipment. We focus on consumer products. Try a consumer-grade equivalent if there is one.",
  financial_product:
    "Octopusden doesn't compare insurance, loans, mortgages, or credit cards. We focus on consumer products you buy and ship home.",
  travel:
    "Octopusden doesn't compare flights, hotels, or holiday packages. We focus on consumer products you buy and ship home.",
  ticket_or_event:
    "Octopusden doesn't compare event tickets. We focus on consumer products you buy and ship home.",
  digital_subscription:
    "Octopusden doesn't compare streaming or software subscriptions. We focus on physical products you buy and ship home — DVDs, books, video game discs are in scope.",
};

function emptyMessageFor(
  reason: string | null,
  query: string,
  productLabel: string,
): string {
  // B-P0-22 (2026-05-17): out-of-scope short-circuit. Phase A returns
  // `empty_reason="out_of_scope:<category>"` for queries Mio deliberately
  // doesn't answer (cars, real estate, services, B2B, etc.).
  if (reason && reason.startsWith("out_of_scope:")) {
    const category = reason.slice("out_of_scope:".length);
    return (
      OUT_OF_SCOPE_COPY[category] ??
      "Octopusden doesn't compare this kind of query yet. We focus on consumer products you buy and ship home."
    );
  }
  switch (reason) {
    case "intent_category_other":
      return `We couldn't tell what kind of product "${query}" is. Try a more specific query like "Sony WH-1000XM5" or "65 inch OLED TV".`;
    case "intent_confidence_below_threshold":
      return `"${query}" is a bit too vague for us to compare retailers reliably. Add a brand or model number and try again.`;
    case "no_retailers_for_category":
      return productLabel
        ? `We don't yet have AU retailers seeded for the kind of product "${productLabel}" is. We're adding new categories every week.`
        : `We don't yet cover that category in Australia. We're adding new categories every week.`;
    case "no_targets_resolved":
      return `We understood you want "${productLabel}" but couldn't resolve a product page on any of our retailers. Try the brand + model number directly.`;
    default:
      return `No results for "${query}". Try a brand + model number.`;
  }
}

function ErrorState({ title, detail }: { title: string; detail: string }) {
  return (
    <main className="relative mx-auto flex min-h-[calc(100vh-160px)] max-w-2xl flex-col items-center justify-center px-6 py-16 text-center">
      <p className="mb-4 text-xs uppercase tracking-[0.2em] text-muted-foreground">
        Something went wrong
      </p>
      <h1 className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl">
        {title}
      </h1>
      <p className="mt-5 max-w-md text-sm leading-relaxed text-muted-foreground">
        {detail}
      </p>
      <Link
        href="/"
        className="mt-8 inline-flex items-center gap-1.5 rounded-xl border border-border/60 bg-secondary/30 backdrop-blur-md px-4 py-2.5 text-sm font-medium text-foreground transition-all duration-150 hover:border-accent/40 hover:bg-secondary/60"
      >
        ← Try a new search
      </Link>
    </main>
  );
}

